import type { MemoryDigest } from '@cortex/types';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import '../../../components/refinement/refinement.css';
import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { WindowPicker, type WindowDays } from '../../../components/common/WindowPicker';
import { useMemoryDigest } from '../../../lib/agent-memory';
import { routes } from '../../../lib/constants';
import { formatDate } from '../../../lib/format';

/**
 * /agents/memories/digest — the review digest (DEV-17253, design §4.7): what
 * agents taught themselves in the window. Once approval is automatic
 * (DEV-17256), after-the-fact review is the only remaining control — this
 * page is what makes the audit trail a trail somebody reads instead of an
 * alibi. Three sections: memories that went live on a policy with no human
 * in the loop, principle-judge promotions, and the trail's per-kind pulse.
 */
function MemoryDigestPage() {
  const [days, setDays] = useState<WindowDays>(7);
  const q = useMemoryDigest(days);

  return (
    <div className="refinement-memory-metrics">
      <PageHeader
        title="What agents taught themselves"
        actions={
          <>
            <BackButton to={routes.MEMORY} label="Memories" />
            <WindowPicker value={days} onChange={setDays} />
          </>
        }
      />

      {q.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-6)' }}>
          <Spinner />
        </div>
      ) : q.isError ? (
        <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)' }}>
          Failed to load digest: {q.error instanceof Error ? q.error.message : 'unknown error'}
        </div>
      ) : q.data ? (
        <DigestBody data={q.data} />
      ) : null}
    </div>
  );
}

function DigestBody({ data }: { data: MemoryDigest }) {
  const quiet = data.autoApproved.length === 0 && data.judgePromotions.length === 0;
  return (
    <div className="refinement-memory-metrics__body">
      {quiet ? (
        <EmptyState
          title="Nothing self-approved in this window"
          description="No memory went live on an auto-approve policy and the judge promoted no principles. A quiet digest is a fine digest — widen the window to look further back."
        />
      ) : null}

      {data.autoApproved.length > 0 ? (
        <section className="refinement-memory-metrics__card">
          <header>
            <h3 className="t-mono-label">Auto-approved (no human in the loop)</h3>
            <span className="t-xs t-muted">{data.autoApproved.length} in window</span>
          </header>
          <table className="refinement-memory-metrics__table">
            <thead>
              <tr>
                <th>Memory</th>
                <th>Type</th>
                <th>Agents</th>
                <th>Policy</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {data.autoApproved.map((m) => (
                <tr key={m.memoryId}>
                  <td className="refinement-memory-metrics__archived-content">{m.content}</td>
                  <td>
                    <span className="badge badge--neutral">{m.type.toLowerCase()}</span>
                  </td>
                  <td>
                    {m.agentIds.map((a) => (
                      <code key={a} className="t-mono t-xs">
                        {a}
                      </code>
                    ))}
                  </td>
                  <td>
                    <code className="t-mono t-xs">{m.approvedByPolicyId ?? '—'}</code>
                  </td>
                  <td className="t-xs t-muted">{formatDate(m.approvedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {data.judgePromotions.length > 0 ? (
        <section className="refinement-memory-metrics__card">
          <header>
            <h3 className="t-mono-label">Judge promotions (new principles)</h3>
            <span className="t-xs t-muted">{data.judgePromotions.length} in window</span>
          </header>
          <ul className="refinement-memory-metrics__archived-list">
            {data.judgePromotions.map((p) => (
              <li key={`${p.memoryId}-${p.promotedAt}`}>
                <span className="badge badge--neutral">principle</span>
                <span className="refinement-memory-metrics__archived-content">{p.content}</span>
                <span className="t-xs t-muted">
                  {p.actorSubject ?? 'judge'} · {formatDate(p.promotedAt)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="refinement-memory-metrics__card refinement-memory-metrics__distro">
        <header>
          <h3 className="t-mono-label">Trail activity by kind</h3>
          <span className="t-xs t-muted">since {formatDate(data.since)}</span>
        </header>
        {Object.keys(data.eventCounts).length === 0 ? (
          <p className="t-xs t-muted">No memory events in this window.</p>
        ) : (
          <ul>
            {Object.entries(data.eventCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([kind, count]) => (
                <li key={kind}>
                  <span>{kind}</span>
                  <span className="t-xs t-muted">{count}</span>
                </li>
              ))}
          </ul>
        )}
      </section>

      <p className="t-xs t-muted">
        Auto-approved rows are governed after the fact: archive one from the{' '}
        <Link to="/agents/memories">memories list</Link> if the fleet taught itself something wrong.
      </p>
    </div>
  );
}

export const Route = createFileRoute('/agents/memories/digest')({
  component: MemoryDigestPage,
});
